import {
  buildActiveModuleFilters,
  isCompositeGroupConfigured,
  type CompositeFilterType,
  type ModuleFilterChange,
  type ModuleQueryController,
} from '@/modules/core/compositeQuery';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { analytics, type SemanticContext } from '../client';
import { beginAction, currentAccountScope, immutableContext, sameAccount } from '../context';
import {
  validateCriteriaSnapshot,
  type AnalyticsSurface,
  type CriteriaDimension,
  type CriteriaProvenance,
  type CriteriaSnapshot,
  type CriterionValue,
  type EntityNamespace,
  type EntityReference,
} from '../contract-v2';

export const FILTER_CRITERIA_VERSION = 'browser_filters_1';
const SNAPSHOT_BYTES = 4800;
const SAMPLE_ITEMS = 20;
type Values = Readonly<Record<string, unknown>>;
export interface FilterOption {
  id?: string | number;
  value?: string | number;
  name?: unknown;
  label?: unknown;
  text?: unknown;
  selected_text?: unknown;
}
/** Safe display metadata lives beside draft state, never inside API business filters. */
export interface FilterMetadata {
  readonly labels?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly provenance?: Readonly<Record<string, CriteriaProvenance>>;
}

declare const filterDraftBrand: unique symbol;
/** Local comparison token. Keep it in memory; use filterSnapshot for event/request data. */
export interface FilterDraftCapture {
  readonly [filterDraftBrand]: true;
}
const capturedDimensions = new WeakMap<FilterDraftCapture, readonly CriteriaDimension[]>();

function captureDimensions(dimensions: readonly CriteriaDimension[]): FilterDraftCapture {
  const capture = Object.freeze({}) as FilterDraftCapture;
  capturedDimensions.set(capture, immutableContext(dimensions));
  return capture;
}

/** Capture complete allowlisted values before sampling. The opaque token cannot serialize them. */
export function captureFilterDraft(
  values: Values,
  metadata: FilterMetadata = {},
): FilterDraftCapture {
  return captureDimensions(normalizeFilterDimensions(values, metadata));
}

function projectFilterDraft(
  capture: FilterDraftCapture,
  project: (dimensions: readonly CriteriaDimension[]) => readonly CriteriaDimension[],
): FilterDraftCapture {
  return captureDimensions(project(capturedDimensions.get(capture) || []));
}

type ModuleQueryState = Pick<
  ModuleQueryController,
  'queryMode' | 'moduleFilters' | 'compositeGroups'
>;
const moduleTypes: CompositeFilterType[] = ['category_of_interest', 'claim_of_interest'];
const groupOriginKey = (type: CompositeFilterType, id: string) => `module_group:${type}:${id}`;

/** Local draft coordinates only. Only filterSnapshot's allowlisted dimensions enter an event. */
export function moduleFilterValues(query?: ModuleQueryState): Values {
  if (!query) return {};
  const groups = query.compositeGroups || {};
  return {
    query_mode: query.queryMode,
    module_filters: buildActiveModuleFilters(
      query.queryMode,
      query.moduleFilters,
      query.compositeGroups,
    ),
    module_group_keys:
      query.queryMode === 'composite'
        ? (Object.keys(groups) as CompositeFilterType[]).flatMap((type) =>
            groups[type]
              .filter((group) => isCompositeGroupConfigured(type, group.value))
              .map((group) => groupOriginKey(type, group.id)),
          )
        : [],
    module_all_group_keys: moduleTypes.flatMap((type) =>
      (groups[type] || []).map((group) => groupOriginKey(type, group.id)),
    ),
    module_group_counts:
      query.queryMode === 'composite'
        ? Object.fromEntries(moduleTypes.map((type) => [type, groups[type]?.length || 0]))
        : {},
  };
}

function pruneModuleOrigins(metadata: FilterMetadata, values: Values): FilterMetadata {
  if (!Array.isArray(values.module_all_group_keys)) return metadata;
  const active = new Set(values.module_all_group_keys);
  return {
    ...metadata,
    provenance: Object.fromEntries(
      Object.entries(metadata.provenance || {}).filter(
        ([key]) => !key.startsWith('module_group:') || active.has(key),
      ),
    ),
  };
}

const namespaces: Record<string, EntityNamespace> = {
  ingredient_ids: 'internal_ingredient',
  application_ids: 'internal_application',
  category_ids: 'internal_category',
  product_category_ids: 'internal_category',
  sub_category_ids: 'internal_subcategory',
  product_ids: 'internal_product',
  product_list_item_ids: 'product_list_item',
  marketer_ids: 'internal_marketer',
  manufacturer_ids: 'internal_manufacturer',
  exclude_marketer_ids: 'internal_marketer',
  exclude_manufacturer_ids: 'internal_manufacturer',
  organizations: 'forager_organization',
  organization_ids: 'forager_organization',
  industries: 'provider_industry',
  industries_exclude: 'provider_industry',
  organization_industries: 'provider_industry',
  locations: 'provider_location',
  person_locations: 'provider_location',
  organization_locations: 'provider_location',
  job_post_locations: 'provider_location',
  job_post_locations_exclude: 'provider_location',
  person_skills: 'provider_skill',
  client_category_ids: 'client_category',
  client_group_ids: 'client_group',
  client_product_ids: 'client_product',
  packaging_type_ids: 'internal_packaging',
  packaging_material_ids: 'internal_packaging',
  inner_package_ids: 'internal_packaging',
  outer_package_ids: 'internal_packaging',
};
const aliases: Record<string, string> = {
  ingredientIds: 'ingredient_ids',
  productIds: 'product_ids',
  productListItemIds: 'product_list_item_ids',
  applicationIds: 'application_ids',
};
const textKeys = new Set(
  `query person_name role_title role_description person_headline
  organization_description job_post_title job_post_description company_type look_for
  company_name company_name_query company_location_query company_country_query
  description claims_features_query size_unit ingredient_match_operator
  funding_types packaging_type_aliases country_of_origin countries_of_origin countries_of_sale
  countries_of_origin_exclude countries_of_sale_exclude domains keywords
  web_technologies linkedin_public_identifiers private_labels brand_names claims
  nutri_score nutri_score_grades closure_material_names simple_event_source simple_event_reason
  locationNames locationSearchTerms locationQuery`
    .split(/\s+/)
    .filter(Boolean),
);
// No namespace is invented for IDs that the shared contract cannot yet represent.
const opaqueIds = new Set(
  `organization_keywords brand_ids uhc_ids company_ids region_ids
  distributed_in_ids formulation_ids package_filter_ids environmental_filter_ids
  shelving_detail_ids closure_material_ids closure_type_ids flavour_ids fragrance_ids
  positioning_ids nutri_score_ids beverage_color_ids claim_interest_ids`
    .split(/\s+/)
    .filter(Boolean),
);
const booleanKeys = new Set([
  'role_is_current',
  'job_post_is_remote',
  'job_post_is_active',
  'matched_only',
]);
const rangeFields: Record<string, { min: string; max: string; unit?: string }> = {
  organization_employees: {
    min: 'organization_employees_start',
    max: 'organization_employees_end',
    unit: 'people',
  },
  organization_revenue: { min: 'organization_revenue_start', max: 'organization_revenue_end' },
  funding_total: { min: 'funding_total_start', max: 'funding_total_end' },
  role_years_on_position: {
    min: 'role_years_on_position_start',
    max: 'role_years_on_position_end',
    unit: 'years',
  },
  role_position_date: {
    min: 'role_position_start_date',
    max: 'role_position_end_date',
    unit: 'date',
  },
  funding_event_date_featured: {
    min: 'funding_event_date_featured_start',
    max: 'funding_event_date_featured_end',
    unit: 'date',
  },
  job_post_date_featured: {
    min: 'job_post_date_featured_start',
    max: 'job_post_date_featured_end',
    unit: 'date',
  },
  simple_event_date_featured: {
    min: 'simple_event_date_featured_start',
    max: 'simple_event_date_featured_end',
    unit: 'date',
  },
  founded_date: { min: 'founded_date_start', max: 'founded_date_end', unit: 'date' },
  employees: { min: 'employees_start', max: 'employees_end', unit: 'people' },
  revenue: { min: 'revenue_start', max: 'revenue_end' },
  domain_rank: { min: 'domain_rank_start', max: 'domain_rank_end' },
  domain_traffic: { min: 'domain_traffic_start', max: 'domain_traffic_end' },
  marketer_team_size: {
    min: 'min_marketer_team_size',
    max: 'max_marketer_team_size',
    unit: 'people',
  },
  manufacturer_team_size: {
    min: 'min_manufacturer_team_size',
    max: 'max_manufacturer_team_size',
    unit: 'people',
  },
  localprice: { min: 'localprice_min', max: 'localprice_max' },
  price_usd: { min: 'price_usd_min', max: 'price_usd_max', unit: 'USD' },
  packsize: { min: 'packsize_min', max: 'packsize_max' },
  wholepacksize: { min: 'wholepacksize_min', max: 'wholepacksize_max' },
  quantity: { min: 'quantity_min', max: 'quantity_max', unit: 'count' },
  alcohol_by_volume: {
    min: 'alcohol_by_volume_min',
    max: 'alcohol_by_volume_max',
    unit: 'percent',
  },
};
const interestKeys = [
  'marketers',
  'manufacturers',
  'brands',
  'ingredients',
  'flavours',
  'fragrances',
  'packaging_types',
  'packaging_materials',
];
const cleanText = (value: string, limit = Infinity) =>
  value
    .replace(/[\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, limit);
const plain = (value: unknown): Record<string, any> => {
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      return plain(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return value && Object.getPrototypeOf(value) === Object.prototype
    ? (value as Record<string, any>)
    : {};
};
const utf8Bytes = (value: unknown) =>
  encodeURIComponent(JSON.stringify(value)).replace(/%[0-9A-F]{2}|./g, 'x').length;
const nonempty = (value: unknown) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  (!Array.isArray(value) || value.length > 0);
const numeric = (value: unknown) =>
  nonempty(value) && ['string', 'number'].includes(typeof value) && Number.isFinite(Number(value))
    ? Number(value)
    : undefined;

export function rememberFilterOptions(
  previous: FilterMetadata | undefined,
  key: string,
  options: FilterOption | readonly FilterOption[] | undefined,
): FilterMetadata {
  if (!(key in namespaces) && !opaqueIds.has(key)) return previous || {};
  const labels = new Map(Object.entries(previous?.labels?.[key] || {}));
  for (const option of Array.isArray(options) ? options : options ? [options] : []) {
    const id = option.value ?? option.id;
    const name = [option.name, option.selected_text, option.text, option.label].find(
      (value) => typeof value === 'string' && value.trim(),
    );
    if (id !== undefined && typeof name === 'string' && String(id) !== name.trim()) {
      // Never stringify React nodes, fallback IDs, whole options, or returned person records.
      labels.delete(String(id));
      labels.set(String(id), cleanText(name, 120));
    }
  }
  return {
    ...previous,
    labels: { ...previous?.labels, [key]: Object.fromEntries([...labels].slice(-100)) },
  };
}

function entityId(key: string, id: string): string {
  const prefix: Record<string, string> = {
    packaging_type_ids: 'type',
    packaging_material_ids: 'material',
    inner_package_ids: 'inner',
    outer_package_ids: 'outer',
  };
  return prefix[key] ? `${prefix[key]}:${id}` : id;
}

const normalizedIds = (raw: unknown) =>
  [
    ...new Set(
      (Array.isArray(raw) ? raw : String(raw).split(','))
        .filter((v) => typeof v === 'string' || (typeof v === 'number' && Number.isSafeInteger(v)))
        .map((v) => String(v).trim())
        .filter((v) => /^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,119}$/.test(v)),
    ),
  ].sort();

// Normalize once for both local comparison and serialization. Nothing is sampled here.
function normalizeFilterDimensions(input: Values, metadata: FilterMetadata): CriteriaDimension[] {
  const source: Record<string, any> = { ...plain(input.filters), ...input };
  Object.entries(aliases).forEach(([alias, key]) => {
    if (source[alias] !== undefined) source[key] = source[alias];
  });
  const dimensions: CriteriaDimension[] = [];
  const provenance = (key: string): CriteriaProvenance =>
    metadata.provenance?.[key] || (key === 'role_is_current' ? 'system_scope' : 'user');
  for (const key of [
    ...new Set([...Object.keys(namespaces), ...opaqueIds, ...textKeys, ...booleanKeys]),
  ].sort()) {
    const raw = source[key];
    if (!nonempty(raw)) continue;
    let value: CriterionValue;
    let entities: EntityReference[] | undefined;
    if (namespaces[key] || opaqueIds.has(key)) {
      const values = normalizedIds(raw);
      if (!values.length) continue;
      value = values;
      dimensions.push({
        key: `${key}_selected_count`,
        value: values.length,
        active: false,
        provenance: provenance(key),
      });
      if (namespaces[key]) {
        entities = value.map((id) => {
          const label = metadata.labels?.[key]?.[id];
          const name =
            typeof label === 'string' && label.trim() !== id ? cleanText(label, 120) : '';
          return {
            id: entityId(key, id),
            namespace: namespaces[key],
            label_resolution_status: name ? 'resolved' : 'unresolved',
            ...(name ? { name, label_source: 'browser_option' as const } : {}),
          };
        });
      }
    } else if (booleanKeys.has(key)) {
      if (![true, false, 'true', 'false'].includes(raw)) continue;
      value = raw === true || raw === 'true';
    } else if (Array.isArray(raw)) {
      value = [
        ...new Set(raw.filter((v) => typeof v === 'string').map((v) => cleanText(v))),
      ].sort();
    } else if (typeof raw === 'string') {
      value = cleanText(raw);
    } else continue;
    dimensions.push({
      key,
      value,
      active: key !== 'role_is_current' || value !== false,
      provenance: provenance(key),
      operator: Array.isArray(value) ? (key.includes('exclude') ? 'not_in' : 'in') : 'eq',
      ...(entities ? { entities } : {}),
    });
  }
  for (const [key, range] of Object.entries(rangeFields)) {
    const date = range.unit === 'date';
    const convert = (v: unknown) =>
      date ? (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined) : numeric(v);
    const min = convert(source[range.min]);
    const max = convert(source[range.max]);
    if (min === undefined && max === undefined) continue;
    // Invalid ranges are blocked by the UI/model, never serialized as accepted criteria.
    if (min !== undefined && max !== undefined && min > max) continue;
    const unit =
      range.unit ||
      ((key === 'packsize' || key === 'wholepacksize') &&
      ['ml', 'g', 'count', 'oz'].includes(source.size_unit)
        ? source.size_unit
        : undefined);
    dimensions.push({
      key,
      value: { ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}) },
      active: true,
      provenance: metadata.provenance?.[key] || provenance(range.min),
      operator: 'range',
      ...(unit ? { unit } : {}),
    });
  }
  for (const key of interestKeys) {
    const enabled = plain(source.interestToggles)[key];
    if (typeof enabled !== 'boolean') continue;
    const dimensionKey = `interest_${key}`;
    const origin = metadata.provenance?.[dimensionKey] || 'portfolio';
    dimensions.push({
      key: dimensionKey,
      value: enabled,
      active: enabled,
      provenance: origin,
      operator: 'eq',
    });
    if (enabled) {
      dimensions.push({
        key: `${dimensionKey}_mode`,
        value: plain(source.interestActions)[key] === 'exclude' ? 'exclude' : 'include',
        active: false,
        provenance: origin,
      });
      const rawSelected = plain(source.interestSelections)[key];
      if (Array.isArray(rawSelected)) {
        const selected = normalizedIds(rawSelected);
        dimensions.push({
          key: `${dimensionKey}_selected_count`,
          value: selected.length,
          active: false,
          provenance: 'user',
        });
        dimensions.push({
          key: `${dimensionKey}_ids`,
          value: selected,
          active: false,
          provenance: 'user',
        });
      }
    }
  }
  // Keep the same namespace mapping as T2: blend/category-group IDs are client groups.
  // Unsupported claim IDs remain explicit unresolved values, never fabricated entities.
  if (source.query_mode === 'simple' || source.query_mode === 'composite') {
    dimensions.push({
      key: 'query_mode',
      value: source.query_mode,
      active: false,
      provenance: metadata.provenance?.query_mode || 'system_scope',
    });
  }
  for (const type of moduleTypes) {
    const count = plain(source.module_group_counts)[type];
    if (Number.isSafeInteger(count) && count >= 0) {
      const key = `${type}_group_count`;
      dimensions.push({
        key,
        value: count,
        active: false,
        provenance: metadata.provenance?.[key] || 'system_scope',
      });
    }
  }
  const modules = plain(source.module_filters ?? source.moduleFilters);
  const entries: Array<[string, Record<string, any>]> = [
    'category_of_interest',
    'claim_of_interest',
  ]
    .filter((key) => modules[key])
    .map((key) => [key, plain(modules[key])]);
  const groups = plain(modules.composite_query).groups;
  if (Array.isArray(groups)) {
    groups.forEach((group, index) => entries.push([`composite_${index}`, plain(group)]));
  }
  for (const [key, module] of entries) {
    const groupKey =
      key.startsWith('composite_') && Array.isArray(source.module_group_keys)
        ? source.module_group_keys[Number(key.slice('composite_'.length))]
        : undefined;
    const origin: CriteriaProvenance =
      (groupKey && metadata.provenance?.[groupKey]) ||
      metadata.provenance?.[key] ||
      (module.mode === 'all' || module.mode === 'portfolio' ? 'portfolio' : 'user');
    if (typeof module.mode === 'string')
      dimensions.push({
        key: `${key}_mode`,
        value: cleanText(module.mode),
        active: false,
        provenance: origin,
      });
    if (
      typeof module.type === 'string' &&
      ['category_of_interest', 'claim_of_interest'].includes(module.type)
    ) {
      dimensions.push({
        key: `${key}_type`,
        value: module.type,
        active: false,
        provenance: origin,
      });
    }
    const moduleValues = {
      client_category_ids: module.category_ids,
      client_group_ids: module.category_group_ids ?? module.blend_ids,
      client_product_ids: module.client_product_ids,
      ingredient_ids: module.ingredient_ids,
      claim_interest_ids: module.claim_interest_ids,
    };
    const child = normalizeFilterDimensions(moduleValues, {
      ...metadata,
      provenance: Object.fromEntries(Object.keys(moduleValues).map((field) => [field, origin])),
    });
    dimensions.push(...child.map((d) => ({ ...d, key: `${key}_${d.key}` })));
  }
  return dimensions;
}

function sampleValue(
  value: CriterionValue,
  preferred: readonly unknown[] = [],
  limit = SAMPLE_ITEMS,
): CriterionValue {
  if (typeof value === 'string') return cleanText(value, 256);
  if (!Array.isArray(value)) return value;
  const prioritized = new Set(preferred);
  const values =
    value.length <= limit
      ? value
      : [
          ...value.filter((item) => prioritized.has(item)),
          ...value.filter((item) => !prioritized.has(item)),
        ];
  return values
    .slice(0, limit)
    .map((item) => (typeof item === 'string' ? cleanText(item, 256) : item)) as CriterionValue;
}

function valueTruncated(full: CriterionValue, sample: CriterionValue): boolean {
  return Array.isArray(full)
    ? !Array.isArray(sample) ||
        full.length > sample.length ||
        full.some((v) => typeof v === 'string' && v.length > 256)
    : typeof full === 'string' && full !== sample;
}

/** Frontend submitted display state. This never claims to be backend effective criteria. */
export function filterSnapshot(input: Values, metadata: FilterMetadata = {}): CriteriaSnapshot {
  let truncated = false;
  let unresolved = 0;
  let resolved = 0;
  const dimensions = normalizeFilterDimensions(input, metadata)
    .filter((dimension) => {
      const index = /^composite_(\d+)_/.exec(dimension.key)?.[1];
      if (index && Number(index) >= 20) {
        truncated = true;
        return false;
      }
      return true;
    })
    .map((dimension) => {
      const value = sampleValue(dimension.value);
      truncated ||= valueTruncated(dimension.value, value);
      const entities = dimension.entities?.slice(0, SAMPLE_ITEMS);
      if (entities) {
        resolved += entities.filter(
          (entity) => entity.label_resolution_status === 'resolved',
        ).length;
        unresolved += entities.filter(
          (entity) => entity.label_resolution_status !== 'resolved',
        ).length;
      } else if (Array.isArray(value) && isOpaqueDimension(dimension.key)) {
        unresolved += value.length;
      }
      return { ...dimension, value, ...(entities ? { entities } : {}) };
    });
  const allFilterKeys = dimensions
    .filter((dimension) => dimension.active)
    .map((dimension) => dimension.key);
  const filterKeys = allFilterKeys.slice(0, 100);
  // Preserve cardinalities first, then safe samples. Both nested search snapshots fit one event.
  const bounded = [
    ...dimensions.filter((d) => d.key.endsWith('_selected_count')),
    ...dimensions.filter((d) => !d.key.endsWith('_selected_count')),
  ].slice(0, 100);
  let snapshot: CriteriaSnapshot = {
    criteria_version: FILTER_CRITERIA_VERSION,
    dimensions: bounded,
    filter_keys: filterKeys,
    filter_count: filterKeys.length,
    label_resolution_status: unresolved ? (resolved ? 'partial' : 'unresolved') : 'resolved',
    unresolved_count: unresolved,
    included_count: bounded.reduce(
      (count, d) => count + (Array.isArray(d.value) ? d.value.length : 0),
      0,
    ),
    truncated:
      truncated ||
      bounded.length !== dimensions.length ||
      filterKeys.length !== allFilterKeys.length,
  };
  while (utf8Bytes(snapshot) > SNAPSHOT_BYTES && (bounded.length || filterKeys.length)) {
    if (bounded.length) bounded.pop();
    else filterKeys.pop();
    snapshot = {
      ...snapshot,
      truncated: true,
      filter_count: filterKeys.length,
      included_count: bounded.reduce(
        (count, d) => count + (Array.isArray(d.value) ? d.value.length : 0),
        0,
      ),
    };
  }
  return immutableContext(snapshot);
}

function isOpaqueDimension(key: string): boolean {
  return (
    [...opaqueIds].some((idKey) => key === idKey || key.endsWith(`_${idKey}`)) ||
    (key.startsWith('interest_') && key.endsWith('_ids'))
  );
}

export function invalidFilterRanges(input: Values): string[] {
  const source = { ...plain(input.filters), ...input };
  return Object.entries(rangeFields)
    .filter(([, range]) => {
      const min = source[range.min];
      const max = source[range.max];
      if (!nonempty(min) && !nonempty(max)) return false;
      if (range.unit === 'date')
        return (
          [min, max].some(
            (v) => nonempty(v) && (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)),
          ) ||
          (nonempty(min) && nonempty(max) && String(min) > String(max))
        );
      return (
        [min, max].some((v) => nonempty(v) && (numeric(v) === undefined || Number(v) < 0)) ||
        (nonempty(min) && nonempty(max) && Number(min) > Number(max))
      );
    })
    .map(([key]) => key);
}

function changeType(
  previous: CriterionValue,
  current: CriterionValue,
): 'add' | 'remove' | 'clear' | 'replace' | 'toggle' | 'range' {
  if (!nonempty(current)) return 'clear';
  if (typeof current === 'boolean') return 'toggle';
  if (current && typeof current === 'object' && !Array.isArray(current)) return 'range';
  if (Array.isArray(previous) && Array.isArray(current)) {
    const before = new Set(previous);
    const after = new Set(current);
    if (previous.every((item) => after.has(item))) return 'add';
    if (current.every((item) => before.has(item))) return 'remove';
  }
  return !nonempty(previous) ? 'add' : 'replace';
}

function changedItems(value: CriterionValue, other: CriterionValue): readonly unknown[] {
  const unchanged = new Set(Array.isArray(other) ? other : []);
  return Array.isArray(value) ? value.filter((item) => !unchanged.has(item)) : [];
}

function selectedEntities(dimension: CriteriaDimension | undefined, ids: readonly unknown[]) {
  const selected = new Set(ids);
  const values = dimension?.value;
  return Array.isArray(values)
    ? (dimension?.entities || []).filter((_, index) => selected.has(values[index]))
    : [];
}

function uniqueEntities(entities: readonly EntityReference[]): EntityReference[] {
  const selected = new Map<string, EntityReference>();
  for (const entity of entities) {
    const key = `${entity.namespace}:${entity.id}`;
    if (!selected.has(key) || (!selected.get(key)?.name && entity.name)) selected.set(key, entity);
  }
  return [...selected.values()];
}

function boundedFilterChange(
  surface: AnalyticsSurface,
  key: string,
  before: CriteriaDimension | undefined,
  after: CriteriaDimension | undefined,
) {
  const previous = before?.value ?? null;
  const current = after?.value ?? null;
  const removed = changedItems(previous, current);
  const added = changedItems(current, previous);
  const allEntities = uniqueEntities([...(before?.entities || []), ...(after?.entities || [])]);
  // Re-sample under the byte budget, retaining the changed members first. This also
  // bounds long UTF-8 labels and two disjoint 20-item selections in a single event.
  for (let limit = SAMPLE_ITEMS; ; limit -= 1) {
    const previousValue = sampleValue(previous, removed, limit);
    const currentValue = sampleValue(current, added, limit);
    const entities =
      allEntities.length <= limit
        ? allEntities
        : uniqueEntities([
            ...selectedEntities(after, added.slice(0, limit)),
            ...selectedEntities(before, removed.slice(0, limit)),
            ...selectedEntities(after, Array.isArray(currentValue) ? currentValue : []),
            ...selectedEntities(before, Array.isArray(previousValue) ? previousValue : []),
          ]).slice(0, limit);
    const truncated =
      valueTruncated(previous, previousValue) ||
      valueTruncated(current, currentValue) ||
      entities.length < allEntities.length;
    const properties = {
      surface,
      filter_key: key,
      change_type: changeType(previous, current),
      previous_value: previousValue,
      current_value: currentValue,
      provenance: after?.provenance || before?.provenance || 'user',
      ...(entities.length ? { entities } : {}),
      ...(truncated ? { properties_truncated: true } : {}),
    };
    if (utf8Bytes(properties) <= SNAPSHOT_BYTES || limit === 1) return properties;
  }
}

/** Compare opaque complete captures. Bounded CriteriaSnapshots cannot establish change identity. */
export function recordFilterDraftChanged(input: {
  surface: AnalyticsSurface;
  previous: FilterDraftCapture;
  current: FilterDraftCapture;
  context?: SemanticContext;
}): void {
  const beforeDimensions = capturedDimensions.get(input.previous);
  const afterDimensions = capturedDimensions.get(input.current);
  // Fail closed for JS callers or copied/serialized tokens; never infer a commit
  // from two equal bounded samples or turn an analytics misuse into a UI failure.
  if (!beforeDimensions || !afterDimensions) return;
  const previous = new Map(beforeDimensions.map((d) => [d.key, d]));
  const current = new Map(afterDimensions.map((d) => [d.key, d]));
  const keys = [...new Set([...previous.keys(), ...current.keys()])];
  const action = beginAction({ surface: input.surface, context: input.context });
  if (!action) return;
  for (const key of keys) {
    if (key.endsWith('_selected_count')) continue;
    const before = previous.get(key);
    const after = current.get(key);
    if (JSON.stringify(before?.value ?? null) === JSON.stringify(after?.value ?? null)) continue;
    analytics.trackSemantic(
      'filter_changed',
      { ...input.context, ...action },
      boundedFilterChange(input.surface, key, before, after),
      { factKey: action ? `${action.action_id}:${key}` : undefined },
    );
  }
}

export function recordFiltersApplied(input: {
  surface: AnalyticsSurface;
  criteria: CriteriaSnapshot;
  context?: SemanticContext;
  reason?: 'apply' | 'reset';
}): void {
  const action = input.context?.action_id
    ? input.context
    : beginAction({
        surface: input.surface,
        context: input.context,
        trigger: 'apply_filters',
      });
  if (!action?.action_id || !validateCriteriaSnapshot(input.criteria).valid) return;
  analytics.trackSemantic(
    'filters_applied',
    { ...input.context, ...action },
    {
      surface: input.surface,
      submitted_criteria: input.criteria,
      criteria_version: input.criteria.criteria_version,
      action_id: action.action_id,
      apply_reason: input.reason || 'apply',
    },
    { factKey: action.action_id },
  );
}

export function recordFilterApplyBlocked(
  surface: AnalyticsSurface,
  reason: string,
  keys: string[] = [],
  context?: SemanticContext,
): void {
  const action = beginAction({ surface, context, trigger: 'apply_filters' });
  analytics.trackSemantic(
    'filter_apply_blocked',
    { ...context, ...action },
    {
      surface,
      reason,
      filter_keys: keys.slice(0, 100),
    },
    { factKey: action?.action_id },
  );
}

/** Call commit after the real callback accepts state, not from effects observing arbitrary changes. */
export function useFilterDraft(input: {
  surface: AnalyticsSurface;
  values: Values;
  metadata?: FilterMetadata;
  context?: SemanticContext;
  onMetadata?: (metadata: FilterMetadata) => void;
  enabled?: boolean;
}) {
  const callbackScope = currentAccountScope();
  const scopeKey = JSON.stringify(callbackScope);
  const pending = useRef<{ previous: FilterDraftCapture; context: SemanticContext }>();
  const pendingOwner = useRef(scopeKey);
  if (pendingOwner.current !== scopeKey) {
    pending.current = undefined;
    pendingOwner.current = scopeKey;
  }
  const current = useRef({ values: input.values, metadata: input.metadata || {} });
  const lastProps = useRef(input.values);
  if (lastProps.current !== input.values) {
    current.current.values = input.values;
    lastProps.current = input.values;
  }
  const metadataProp = useRef(input.metadata);
  const account = useRef(currentAccountScope());
  if (account.current && !sameAccount(account.current, currentAccountScope())) {
    current.current.metadata = input.metadata || {};
    account.current = currentAccountScope();
  }
  if (metadataProp.current !== input.metadata) {
    current.current.metadata = input.metadata || {};
    metadataProp.current = input.metadata;
  }
  const config = useRef(input);
  config.current = input;
  useLayoutEffect(() => {
    const accepted = pending.current;
    if (!accepted) return;
    pending.current = undefined;
    current.current.metadata = pruneModuleOrigins(current.current.metadata, input.values);
    config.current.onMetadata?.(current.current.metadata);
    if (input.enabled === false) return;
    const asUser = (capture: FilterDraftCapture) =>
      projectFilterDraft(capture, (dimensions) =>
        dimensions.map((dimension) => ({ ...dimension, provenance: 'user' })),
      );
    recordFilterDraftChanged({
      surface: input.surface,
      context: accepted.context,
      previous: asUser(accepted.previous),
      current: asUser(captureFilterDraft(input.values, current.current.metadata)),
    });
  });
  // Reset is owned by a parent/reducer and may retain mode or enforced criteria.
  // Capture intent before invoking it, then compare the actual next committed props.
  const commitAfterRender = useCallback(
    (accept: () => void) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      pending.current ||= {
        previous: captureFilterDraft(current.current.values, current.current.metadata),
        context: { ...config.current.context, accountScope: callbackScope },
      };
      try {
        accept();
      } catch (error) {
        pending.current = undefined;
        throw error;
      }
    },
    [scopeKey],
  );
  const remember = useCallback(
    (key: string, options: FilterOption | readonly FilterOption[] | undefined) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      current.current.metadata = rememberFilterOptions(current.current.metadata, key, options);
    },
    [scopeKey],
  );
  const commit = useCallback(
    (values: Values, provenance: Record<string, CriteriaProvenance> = {}) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      const metadata = pruneModuleOrigins(
        {
          ...current.current.metadata,
          provenance: { ...current.current.metadata.provenance, ...provenance },
        },
        values,
      );
      const previous = captureFilterDraft(current.current.values, current.current.metadata);
      const next = captureFilterDraft(values, metadata);
      current.current = { values, metadata };
      config.current.onMetadata?.(metadata);
      const invalid = invalidFilterRanges(values);
      if (config.current.enabled !== false)
        recordFilterDraftChanged({
          surface: config.current.surface,
          previous: projectFilterDraft(previous, (dimensions) =>
            dimensions.filter((d) => !invalid.includes(d.key)),
          ),
          current: next,
          context: config.current.context,
        });
    },
    [scopeKey],
  );
  const sync = useCallback(
    (values: Values, provenance: Record<string, CriteriaProvenance> = {}) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      current.current = {
        values,
        metadata: pruneModuleOrigins(
          {
            ...current.current.metadata,
            provenance: { ...current.current.metadata.provenance, ...provenance },
          },
          values,
        ),
      };
      config.current.onMetadata?.(current.current.metadata);
    },
    [scopeKey],
  );
  return {
    remember,
    commit,
    commitAfterRender,
    sync,
    snapshot: () => filterSnapshot(current.current.values, current.current.metadata),
    capture: () => captureFilterDraft(current.current.values, current.current.metadata),
  };
}

type ModuleMethod =
  | 'setQueryMode'
  | 'updateModuleFilter'
  | 'updateCompositeGroup'
  | 'addCompositeGroup'
  | 'removeCompositeGroup';
type ModuleCommit = {
  method: ModuleMethod;
  type?: CompositeFilterType;
  id?: string;
  change: ModuleFilterChange;
  before: FilterDraftCapture;
  beforeValues: Values;
  context: SemanticContext;
};

/** Scope a callback's diff to its actual control. Removing a composite row must not
 * report the next row's shifted index as a replacement of its selected entities. */
function moduleCommitDimensions(
  captured: readonly CriteriaDimension[],
  values: Values,
  commit: ModuleCommit,
  eventIndex: number,
): CriteriaDimension[] {
  if (commit.method === 'setQueryMode') return captured.filter((d) => d.key === 'query_mode');
  const countKey = `${commit.type}_group_count`;
  if (commit.method === 'addCompositeGroup') return captured.filter((d) => d.key === countKey);
  if (commit.method === 'updateModuleFilter') {
    return captured.filter((d) => d.key.startsWith(`${commit.type}_`) && d.key !== countKey);
  }
  const keys = Array.isArray(values.module_group_keys) ? values.module_group_keys : [];
  const index = keys.indexOf(groupOriginKey(commit.type!, commit.id!));
  const prefix = `composite_${index}_`;
  const dimensions =
    index < 0
      ? []
      : captured
          .filter((d) => d.key.startsWith(prefix))
          .map((d) => ({ ...d, key: `composite_${eventIndex}_${d.key.slice(prefix.length)}` }));
  if (commit.method === 'removeCompositeGroup') {
    dimensions.push(...captured.filter((d) => d.key === countKey));
  }
  return dimensions;
}

function moduleCommitPair(current: FilterDraftCapture, values: Values, commit: ModuleCommit) {
  const currentKeys = Array.isArray(values.module_group_keys) ? values.module_group_keys : [];
  const oldKeys = Array.isArray(commit.beforeValues.module_group_keys)
    ? commit.beforeValues.module_group_keys
    : [];
  const key = commit.type && commit.id ? groupOriginKey(commit.type, commit.id) : '';
  const currentIndex = currentKeys.indexOf(key);
  const eventIndex = currentIndex >= 0 ? currentIndex : oldKeys.indexOf(key);
  const project = (capture: FilterDraftCapture, state: Values) =>
    projectFilterDraft(capture, (dimensions) =>
      moduleCommitDimensions(dimensions, state, commit, eventIndex).map((d) => ({
        ...d,
        provenance: 'user',
      })),
    );
  return {
    previous: project(commit.before, commit.beforeValues),
    current: project(current, values),
  };
}

/** The shared controls supply explicit origins. Queue from those callbacks and read
 * the real committed controller state in layout; never infer user intent from effects.
 * Business reducers/controllers remain the sole authority for accepted state changes. */
export function useModuleFilterController(
  controller: ModuleQueryController | undefined,
  input: {
    surface: AnalyticsSurface;
    values: Values;
    draft: ReturnType<typeof useFilterDraft>;
    metadata?: FilterMetadata;
    context?: SemanticContext;
    enabled?: boolean;
  },
): ModuleQueryController | undefined {
  const account = currentAccountScope();
  const scopeKey = JSON.stringify(account);
  const pending = useRef(new Map<string, ModuleCommit>());
  const owner = useRef(scopeKey);
  if (owner.current !== scopeKey) {
    pending.current.clear();
    owner.current = scopeKey;
  }
  const latest = useRef({ controller, input });
  latest.current = { controller, input };
  useLayoutEffect(() => {
    if (!controller || !pending.current.size) return;
    const commits = [...pending.current.values()];
    pending.current.clear(); // StrictMode replay and metadata-only rerenders have no pending intent.
    const provenance: Record<string, CriteriaProvenance> = {};
    for (const commit of commits) {
      for (const [key, options] of Object.entries(commit.change.options || {})) {
        // Remember samples AND changed members from the complete scoped state.
        // A large option dictionary cannot evict the label of a changed 21st ID.
        const pair = moduleCommitPair(input.draft.capture(), input.values, commit);
        const before = new Map(capturedDimensions.get(pair.previous)!.map((d) => [d.key, d]));
        const after = new Map(capturedDimensions.get(pair.current)!.map((d) => [d.key, d]));
        const selected = new Set<string>();
        for (const field of new Set([...before.keys(), ...after.keys()])) {
          if (field !== key && !field.endsWith(`_${key}`)) continue;
          const previous = before.get(field)?.value ?? null;
          const current = after.get(field)?.value ?? null;
          for (const sample of [
            sampleValue(previous),
            sampleValue(current),
            sampleValue(previous, changedItems(previous, current)),
            sampleValue(current, changedItems(current, previous)),
          ]) {
            if (Array.isArray(sample)) sample.forEach((id) => selected.add(String(id)));
          }
        }
        input.draft.remember(
          key,
          options?.filter((option) => selected.has(String(option.value))),
        );
      }
      const key =
        commit.method === 'setQueryMode'
          ? 'query_mode'
          : commit.method === 'updateModuleFilter'
          ? commit.type!
          : commit.method === 'updateCompositeGroup'
          ? groupOriginKey(commit.type!, commit.id!)
          : `${commit.type}_group_count`;
      provenance[key] = commit.change.source;
      if (commit.method === 'setQueryMode' && controller.queryMode === 'composite') {
        for (const type of moduleTypes) {
          const first = controller.compositeGroups[type]?.[0];
          if (!first) continue;
          const groupKey = groupOriginKey(type, first.id);
          if (!input.metadata?.provenance?.[groupKey]) {
            const previous = capturedDimensions
              .get(commit.before)
              ?.find((d) => d.key.startsWith(`${type}_`));
            if (previous) provenance[groupKey] = previous.provenance;
          }
        }
      }
    }
    input.draft.sync(input.values, provenance);
    const current = input.draft.capture();
    if (input.enabled === false) return;
    for (const commit of commits) {
      if (commit.change.source !== 'user') continue;
      recordFilterDraftChanged({
        surface: input.surface,
        context: commit.context,
        ...moduleCommitPair(current, input.values, commit),
      });
    }
  });

  return useMemo(() => {
    if (!controller) return undefined;
    const invoke = (method: ModuleMethod, args: any[], change?: ModuleFilterChange) => {
      if (account && !sameAccount(account, currentAccountScope())) return;
      const active = latest.current;
      if (!active.controller) return;
      if (change) {
        const type = method === 'setQueryMode' ? undefined : (args[0] as CompositeFilterType);
        const id =
          method === 'updateCompositeGroup' || method === 'removeCompositeGroup'
            ? args[1]
            : undefined;
        const key = `${method}:${type || ''}:${id || ''}`;
        const previous = pending.current.get(key);
        pending.current.set(key, {
          method,
          type,
          id,
          change,
          before: previous?.before || active.input.draft.capture(),
          beforeValues: previous?.beforeValues || active.input.values,
          context: { ...active.input.context, accountScope: account },
        });
      }
      try {
        (active.controller[method] as (...values: any[]) => void)(...args, change);
      } catch (error) {
        pending.current.clear();
        throw error;
      }
    };
    return {
      ...controller,
      analyticsSemantic: input.enabled !== false,
      setQueryMode: (mode, change) => invoke('setQueryMode', [mode], change),
      updateModuleFilter: (type, value, change) =>
        invoke('updateModuleFilter', [type, value], change),
      updateCompositeGroup: (type, id, value, change) =>
        invoke('updateCompositeGroup', [type, id, value], change),
      addCompositeGroup: (type, change) => invoke('addCompositeGroup', [type], change),
      removeCompositeGroup: (type, id, change) =>
        invoke('removeCompositeGroup', [type, id], change),
    };
  }, [controller, scopeKey, input.enabled]);
}

/** Adapts the existing Dva filter callbacks. Only explicit callback dispatches use this;
 * initialization/scope effects keep the original dispatch. Consecutive bound updates
 * in one range callback commit one semantic range after the synchronous state writes. */
export function useFilterActionDispatch(
  dispatch: (action: any) => any,
  input: {
    namespace: string;
    surface: AnalyticsSurface;
    values: Values;
    metadata?: FilterMetadata;
    context?: SemanticContext;
  },
) {
  const callbackScope = currentAccountScope();
  const scopeKey = JSON.stringify(callbackScope);
  const props = useRef({ dispatch, input });
  props.current = { dispatch, input };
  const local = useRef({ values: input.values, metadata: input.metadata || {} });
  const previousProps = useRef(input.values);
  const pending = useRef<{ before: FilterDraftCapture; context: SemanticContext }>();
  const owner = useRef(scopeKey);
  if (owner.current !== scopeKey) {
    pending.current = undefined;
    owner.current = scopeKey;
    local.current = { values: input.values, metadata: input.metadata || {} };
  }
  if (previousProps.current !== input.values && !pending.current) {
    local.current.values = input.values;
    local.current.metadata = input.metadata || {};
    previousProps.current = input.values;
  }
  const remember = useCallback(
    (key: string, options: FilterOption | readonly FilterOption[] | undefined) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      local.current.metadata = rememberFilterOptions(local.current.metadata, key, options);
    },
    [scopeKey],
  );
  const committed = useCallback(
    (action: any) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      const { namespace, surface, context } = props.current.input;
      const method = String(action.type).startsWith(`${namespace}/`)
        ? String(action.type).slice(namespace.length + 1)
        : '';
      const before = local.current.values;
      const filters = { ...plain(before.filters) };
      let values: Record<string, unknown> | undefined;
      const changed: Record<string, CriteriaProvenance> = {};
      if (method === 'updateFilter') {
        const { field, value } = action.payload;
        if (value === undefined || value === null || value === '') delete filters[field];
        else filters[field] = value;
        changed[field] = 'user';
        values = { ...before, filters };
      } else if (method === 'updateFilters') {
        values = { ...before, filters: action.payload };
        for (const key of new Set([...Object.keys(filters), ...Object.keys(action.payload)])) {
          if (JSON.stringify(filters[key]) !== JSON.stringify(action.payload[key]))
            changed[key] = 'user';
        }
      } else if (method === 'clearFilter') {
        for (const key of Array.isArray(action.payload) ? action.payload : [action.payload]) {
          delete filters[key];
          changed[key] = 'user';
        }
        values = { ...before, filters };
        if (JSON.stringify(filters) === JSON.stringify(plain(before.filters))) return;
        action = { ...action, analytics: { trigger: 'apply_filters', ...action.analytics } };
      } else if (method === 'updateInterestToggle' || method === 'updateInterestSelection') {
        const { key } = action.payload;
        if (interestKeys.includes(key)) {
          const selections = { ...plain(before.interestSelections) };
          if (method === 'updateInterestSelection') selections[key] = action.payload.selectedIds;
          else delete selections[key];
          changed[`interest_${key}`] = 'user';
          values = {
            ...before,
            interestToggles: {
              ...plain(before.interestToggles),
              [key]: method === 'updateInterestSelection' || action.payload.value === true,
            },
            interestActions: {
              ...plain(before.interestActions),
              [key]: action.payload.action || 'include',
            },
            interestSelections: selections,
          };
        }
      } else if (method === 'resetFilters') {
        values = {
          filters: {},
          ...(before.interestToggles
            ? {
                interestToggles: Object.fromEntries(interestKeys.map((key) => [key, false])),
              }
            : {}),
        };
      }
      if (!values) return props.current.dispatch(action);
      if (!pending.current) {
        pending.current = {
          before: captureFilterDraft(before, local.current.metadata),
          context: { ...context, accountScope: currentAccountScope() },
        };
        const queued = pending.current;
        queueMicrotask(() => {
          if (pending.current !== queued) return;
          pending.current = undefined;
          const invalid = invalidFilterRanges(local.current.values);
          recordFilterDraftChanged({
            surface,
            previous: projectFilterDraft(queued.before, (dimensions) =>
              dimensions.filter((d) => !invalid.includes(d.key)),
            ),
            current: captureFilterDraft(local.current.values, local.current.metadata),
            context: queued.context,
          });
        });
      }
      local.current = {
        values,
        metadata: {
          ...local.current.metadata,
          provenance: { ...local.current.metadata.provenance, ...changed },
        },
      };
      props.current.dispatch({
        type: `${namespace}/updateState`,
        payload: { filterMetadata: local.current.metadata },
      });
      return props.current.dispatch(action);
    },
    [scopeKey],
  );
  const system = useCallback(
    (action: any) => {
      if (callbackScope && !sameAccount(callbackScope, currentAccountScope())) return;
      const { namespace } = props.current.input;
      const changed: Record<string, CriteriaProvenance> = {};
      if (action.type === `${namespace}/updateFilter`)
        changed[action.payload.field] = action.analyticsProvenance || 'system_scope';
      if (action.type === `${namespace}/updateFilters`) {
        for (const key of Object.keys(action.payload)) {
          if (
            JSON.stringify(action.payload[key]) !==
            JSON.stringify(plain(local.current.values.filters)[key])
          ) {
            changed[key] = action.analyticsProvenance || 'system_scope';
          }
        }
      }
      if (action.type === `${namespace}/updateInterestToggle`)
        changed[`interest_${action.payload.key}`] = 'portfolio';
      if (Object.keys(changed).length) {
        local.current.metadata = {
          ...local.current.metadata,
          provenance: { ...local.current.metadata.provenance, ...changed },
        };
        props.current.dispatch({
          type: `${namespace}/updateState`,
          payload: { filterMetadata: local.current.metadata },
        });
      }
      const result = props.current.dispatch(action);
      if (action.type === `${namespace}/updateFilter`) {
        const filters = {
          ...plain(local.current.values.filters),
          [action.payload.field]: action.payload.value,
        };
        local.current.values = { ...local.current.values, filters };
      } else if (action.type === `${namespace}/updateFilters`) {
        local.current.values = { ...local.current.values, filters: action.payload };
      }
      return result;
    },
    [scopeKey],
  );
  return Object.assign(committed, { remember, system });
}
